import React, { useEffect, useState } from 'react';
import { matchReferenceDeals } from '../api/catalogs';
import { describeError } from '../lib/describeError';
import { formatUSD, formatSamples } from '../lib/format';

// "Closest comparable deals" panel on Step 5 (US5, FR-034..036).
// Hits /api/v1/reference-deals/match with the user's current disease /
// data types / cohort size / per-patient pricing; renders the top 1–2
// matches plus a color-coded comparison line.
export default function ClosestDeals({ inputs, result }) {
  const [state, setState] = useState({ status: 'loading' });

  const samples = inputs?.samples;
  const dataTypes = (inputs?.dataTypes || []).join(',');
  const disease = inputs?.diseaseCategory || 'none';
  const perPatient = samples > 0 && result?.pharmaAnnual ? result.pharmaAnnual / samples : null;

  useEffect(() => {
    let cancelled = false;
    matchReferenceDeals({
      disease,
      dataTypes: inputs?.dataTypes || [],
      samples,
      perPatientUsd: perPatient,
    })
      .then((data) => { if (!cancelled) setState({ status: 'ok', data }); })
      .catch((err) => { if (!cancelled) setState({ status: 'error', code: err?.code || 'match_failed' }); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disease, dataTypes, samples, perPatient]);

  return (
    <div style={cardStyle}>
      <div style={headStyle}>
        <BenchmarkIcon />
        Closest comparable deals
      </div>

      {state.status === 'loading' && <p style={loadingStyle}>Looking up benchmarks…</p>}

      {state.status === 'error' && (
        <p style={errorStyle}>{describeError('match', state.code)}</p>
      )}

      {state.status === 'ok' && state.data.matches?.length > 0 && (
        <>
          <div style={dealListStyle}>
            {state.data.matches.map((m) => (
              <DealRow key={m.Deal?.Name || m.Score} deal={m.Deal} />
            ))}
          </div>
          {state.data.comparison && (
            <ComparisonLine cmp={state.data.comparison} />
          )}
        </>
      )}
    </div>
  );
}

function DealRow({ deal }) {
  if (!deal) return null;
  return (
    <div style={dealRowStyle}>
      <div style={dealHeadStyle}>
        <span style={dealNameStyle}>{deal.Name}</span>
        <span style={dealYearStyle}>{deal.Year}</span>
      </div>
      <div style={dealStatsStyle}>
        <span style={dealStatStyle}>
          <strong>{formatUSD(deal.PerPatientUSD)}</strong> / patient
        </span>
        <span style={dealStatSepStyle}>·</span>
        <span style={dealStatStyle}>
          <strong>{formatSamples(deal.Patients)}</strong> patients
        </span>
        <span style={dealStatSepStyle}>·</span>
        <span style={dealTierStyle}>{deal.Tier}</span>
      </div>
      {deal.Note && <p style={dealNoteStyle}>{deal.Note}</p>}
    </div>
  );
}

function ComparisonLine({ cmp }) {
  const tone = COLOR_TONES[cmp.color_indicator] || COLOR_TONES.green;
  return (
    <div style={{ ...comparisonStyle, background: tone.bg, color: tone.fg }}>
      <span style={{ ...comparisonDotStyle, background: tone.dot }} aria-hidden="true" />
      <div style={{ flex: 1 }}>
        <div style={comparisonHeadStyle}>
          Your {formatUSD(cmp.user_per_patient_usd)} / patient · closest {formatUSD(cmp.closest_per_patient_usd)} ·{' '}
          {cmp.ratio.toFixed(2)}×
        </div>
        <div style={comparisonNoteStyle}>{cmp.note}</div>
      </div>
    </div>
  );
}

function BenchmarkIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M3 3v18h18" stroke="currentColor" strokeWidth="1.6" fill="none" strokeLinecap="round" />
      <path d="M7 14l4-4 4 4 5-5" stroke="currentColor" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const COLOR_TONES = {
  green: { bg: 'var(--accent-bg)', fg: 'var(--accent-strong)', dot: 'var(--accent)' },
  yellow: { bg: 'var(--amber-bg)', fg: 'var(--amber-strong)', dot: 'var(--amber)' },
  red: { bg: '#fee2e2', fg: '#991b1b', dot: 'var(--error)' },
};

const cardStyle = { padding: '22px 24px', background: 'var(--paper)', border: '1px solid var(--line)', borderRadius: '4px' };
const headStyle = { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12.5px', color: 'var(--amber)', fontWeight: 500, marginBottom: '16px' };

const loadingStyle = { margin: 0, fontSize: '13px', color: 'var(--muted)' };
const errorStyle = { margin: 0, fontSize: '12.5px', color: 'var(--amber)' };

const dealListStyle = { display: 'flex', flexDirection: 'column', gap: '12px' };
const dealRowStyle = { paddingBottom: '12px', borderBottom: '1px solid var(--line)' };
const dealHeadStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '12px' };
const dealNameStyle = { fontSize: '14px', fontWeight: 600, color: 'var(--ink)' };
const dealYearStyle = { fontFamily: 'var(--mono)', fontSize: '11px', color: 'var(--muted)' };
const dealStatsStyle = { display: 'flex', alignItems: 'baseline', gap: '8px', marginTop: '6px', fontSize: '12px', color: 'var(--ink2)', flexWrap: 'wrap' };
const dealStatStyle = { fontVariantNumeric: 'tabular-nums' };
const dealStatSepStyle = { color: 'var(--muted)' };
const dealTierStyle = { padding: '1px 8px', borderRadius: '999px', background: 'var(--accent-bg)', color: 'var(--accent)', fontSize: '11px', fontWeight: 500 };
const dealNoteStyle = { margin: '8px 0 0', fontSize: '12.5px', color: 'var(--ink2)', lineHeight: 1.5 };

const comparisonStyle = { marginTop: '14px', padding: '12px 14px', borderRadius: '6px', display: 'flex', alignItems: 'flex-start', gap: '10px' };
const comparisonDotStyle = { width: '10px', height: '10px', borderRadius: '50%', flexShrink: 0, marginTop: '5px' };
const comparisonHeadStyle = { fontSize: '13px', fontWeight: 600 };
const comparisonNoteStyle = { fontSize: '12px', marginTop: '4px', lineHeight: 1.5 };
